import { getFormProps, useForm, useInputControl } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { Form } from '@remix-run/react'
import { match } from 'ts-pattern'

import { Checkbox, InputError, Link, Textarea, TextInput } from '~/components'
import { FieldLabel } from '~/features/common'
import { Availability } from '~/models/types'

import { planContactFormSchema } from './schema'

const budgetInitialValue = (availability: Availability) => {
  return match(availability)
    .with(
      'UNAVAILABLE',
      () => '空きが出たら連絡をいただきたいです。よろしくお願いいたします。',
    )
    .otherwise(
      () =>
        `内見希望です。\n・第1希望 ◯月◯︎日 ◯︎◯時〜◯◯︎時\n・第2希望 ◯月◯︎日 ◯︎◯時〜◯◯︎時`,
    )
}

type FieldWrapperProps = {
  children: React.ReactNode
}

const FieldWrapper = ({ children }: FieldWrapperProps) => {
  return <div className="flex flex-col gap-y-2">{children}</div>
}

type Props = {
  availability: Availability
}

export const PlanContactForm = ({ availability }: Props) => {
  const [form, fields] = useForm({
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: planContactFormSchema })
    },
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    defaultValue: {
      content: budgetInitialValue(availability),
    },
  })

  const checkboxControl = useInputControl(fields.agree)

  return (
    <Form
      className="flex flex-col gap-y-6"
      method="post"
      {...getFormProps(form)}
      data-testid="plan-contact-form"
    >
      <FieldWrapper>
        <FieldLabel htmlFor={fields.name.id} label="お名前" required />
        <TextInput
          errorMessages={fields.name.errors}
          id={fields.name.id}
          name={fields.name.name}
          placeholder="例：山田太郎"
        />
      </FieldWrapper>

      <FieldWrapper>
        <FieldLabel
          htmlFor={fields.organization.id}
          label="貴社名・団体名"
          required
          subText="個人の方は「個人」と記入してください"
        />
        <TextInput
          errorMessages={fields.organization.errors}
          id={fields.organization.id}
          name={fields.organization.name}
          placeholder="例：株式会社TranceTECH Capital"
        />
      </FieldWrapper>

      <FieldWrapper>
        <FieldLabel htmlFor={fields.email.id} label="メールアドレス" required />
        <TextInput
          errorMessages={fields.email.errors}
          id={fields.email.id}
          name={fields.email.name}
          placeholder="例：sample@trancetechcapital.co.jp"
        />
      </FieldWrapper>

      <FieldWrapper>
        <FieldLabel
          htmlFor={fields.phone.id}
          label="電話番号（ハイフンなし）"
          required
        />
        <TextInput
          errorMessages={fields.phone.errors}
          id={fields.phone.id}
          name={fields.phone.name}
          placeholder="例：08012345678"
        />
      </FieldWrapper>

      <FieldWrapper>
        <FieldLabel htmlFor={fields.numberOfPeople.id} label="希望人数" />
        <TextInput
          errorMessages={fields.numberOfPeople.errors}
          id={fields.numberOfPeople.id}
          name={fields.numberOfPeople.name}
          placeholder="例：20名"
        />
      </FieldWrapper>

      <FieldWrapper>
        <FieldLabel
          htmlFor={fields.desiredMoveInDate.id}
          label="入居予定時期"
        />
        <TextInput
          errorMessages={fields.desiredMoveInDate.errors}
          id={fields.desiredMoveInDate.id}
          name={fields.desiredMoveInDate.name}
          placeholder="例：今すぐ"
        />
      </FieldWrapper>

      <FieldWrapper>
        <FieldLabel htmlFor={fields.budget.id} label="賃料予算" />
        <TextInput
          errorMessages={fields.budget.errors}
          id={fields.budget.id}
          name={fields.budget.name}
          placeholder="例：月20万円"
        />
      </FieldWrapper>

      <FieldWrapper>
        <FieldLabel htmlFor={fields.content.id} label="お問い合わせ内容" />
        <Textarea
          defaultValue={fields.content.value}
          errorMessages={fields.content.errors}
          id={fields.content.id}
          name={fields.content.name}
          placeholder="例：オフィスの広さや設備について教えてください"
          rows={8}
        />
      </FieldWrapper>

      <div className="flex flex-col gap-y-1">
        <div className="inline-flex items-center justify-center gap-x-2">
          <Checkbox
            checked={checkboxControl.value === 'on'}
            className="h-5 w-5"
            data-testid="agree-checkbox"
            label=""
            name={fields.agree.name}
            onChange={() =>
              checkboxControl.change(
                checkboxControl.value === 'on' ? 'off' : 'on',
              )
            }
            value={checkboxControl.value}
          />
          <p>
            <Link href="/terms" rel="noopener noreferrer" target="_blank">
              利用規約
            </Link>
            <span>・</span>
            <Link
              href="/privacy-policy"
              rel="noopener noreferrer"
              target="_blank"
            >
              プライバシーポリシー
            </Link>
            <span>に同意</span>
          </p>
        </div>
        {fields.agree.errors && (
          <InputError data-testid="agree-error">
            {fields.agree.errors}
          </InputError>
        )}
      </div>

      <button
        className="h-[60px] w-full rounded bg-cta text-body-bold text-white"
        type="submit"
      >
        送信する
      </button>
    </Form>
  )
}
